'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';

interface LoginInputs {
  email: string;
  password: string;
  remember: boolean;
}

interface LoginFormProps {
  onSwitchToSignup: () => void;
}

export default function LoginForm({ onSwitchToSignup }: LoginFormProps) {
  const router = useRouter();
  const { signIn } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  const {
    register,
    handleSubmit,
    setError,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<LoginInputs>({ defaultValues: { remember: false } });

  const emailValue = watch('email');

  const onSubmit = async (data: LoginInputs) => {
    try {
      const result = await signIn(data.email, data.password);
      toast.success('Welcome back to MuyuLink!');

      const supabase = createClient();
      const userId = result?.user?.id;
      let role = 'customer';
      let customerType = 'parcel_sender';

      if (userId) {
        try {
          const { data: profileData } = await supabase
            .from('user_profiles')
            .select('role, is_admin, customer_type')
            .eq('id', userId)
            .single();
          role = profileData?.is_admin ? 'admin' : profileData?.role || 'customer';
          customerType = profileData?.customer_type || 'parcel_sender';
        } catch {
          role = result?.user?.user_metadata?.role || 'customer';
        }
      }

      if (role === 'admin') {
        router.push('/admin-moderation');
      } else if (role === 'traveler') {
        router.push('/traveler-dashboard');
      } else if (role === 'customer' && customerType === 'storage_booker') {
        router.push('/storage-customer-panel');
      } else {
        router.push('/');
      }
      router.refresh();
    } catch (err: any) {
      setError('root', {
        message: err?.message || 'Invalid credentials. Please check your email and password.',
      });
    }
  };

  const handleForgotPassword = async () => {
    const email = emailValue?.trim();
    if (!email) {
      toast.error('Please enter your email address first.');
      return;
    }
    setResetLoading(true);
    try {
      const supabase = createClient();
      const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || 'https://packride8098.builtwithrocket.new';
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${siteUrl}/auth/callback?next=/account-settings`,
      });
      if (error) throw error;
      setResetSent(true);
      toast.success('Password reset email sent! Check your inbox.');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to send reset email. Please try again.');
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <h1 className="text-2xl font-700 text-foreground mb-1">Welcome back</h1>
      <p className="page-subtitle mb-6">Sign in to your MuyuLink account</p>

      {errors.root && (
        <div className="error-banner mb-4">
          {errors.root.message}
        </div>
      )}

      {resetSent && (
        <div className="mb-4 px-4 py-3 bg-positive-subtle border border-green-200 rounded-xl text-sm text-positive font-500">
          Password reset email sent! Check your inbox.
        </div>
      )}

      {/* Email */}
      <div className="form-group mb-4">
        <label htmlFor="login-email" className="form-label">Email Address</label>
        <input
          id="login-email"
          type="email"
          placeholder="you@example.com"
          className={`form-input ${errors.email ? 'border-negative focus:border-negative' : ''}`}
          {...register('email', {
            required: 'Email is required',
            pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email address' },
          })}
        />
        {errors.email && <p className="form-error">{errors.email.message}</p>}
      </div>

      {/* Password */}
      <div className="form-group mb-4">
        <label htmlFor="login-password" className="form-label">Password</label>
        <div className="relative">
          <input
            id="login-password"
            type={showPassword ? 'text' : 'password'}
            placeholder="Enter your password"
            className={`form-input pr-10 ${errors.password ? 'border-negative focus:border-negative' : ''}`}
            {...register('password', { required: 'Password is required' })}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
          </button>
        </div>
        {errors.password && <p className="form-error">{errors.password.message}</p>}
      </div>

      {/* Remember + forgot */}
      <div className="flex items-center justify-between mb-6">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            className="w-4 h-4 rounded border-input accent-primary"
            {...register('remember')}
          />
          <span className="text-sm text-muted-foreground">Remember me</span>
        </label>
        <button
          type="button"
          onClick={handleForgotPassword}
          disabled={resetLoading}
          className="text-sm text-primary font-600 hover:underline disabled:opacity-60 flex items-center gap-1"
        >
          {resetLoading && <Loader2 size={12} className="animate-spin" />}
          Forgot password?
        </button>
      </div>

      <button
        type="submit"
        disabled={isSubmitting}
        className="btn-lg btn-primary w-full"
      >
        {isSubmitting ? (
          <>
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            Signing in…
          </>
        ) : (
          'Sign In to MuyuLink'
        )}
      </button>

      <p className="mt-4 text-center text-sm text-muted-foreground">
        New to MuyuLink?{' '}
        <button type="button" onClick={onSwitchToSignup} className="text-primary font-600 hover:underline">
          Create an account
        </button>
      </p>
    </form>
  );
}